import { Accordion, Button, HideWithCSS, Stack } from "@biblioteksentralen/react";
import { useState } from "react";
import { Sliders, X } from "react-feather";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import FilterMenu from "./FilterMenu";
import FilterMenuAccordion from "./FilterMenuAccordion";
import FilterOptions from "./FilterOptions";
import FilterTags from "./FilterTags";
import { Filteredtems } from "./getFilteredItems";
import { Filter, FilterOption, FilterableItem, filterOptionsEqual } from "./useFilter";

type Props<T extends FilterableItem> = {
  filters: Filter<T>[];
  filteredItems: Filteredtems<T>;
};

function FilterButtons<T extends FilterableItem>({ filters, filteredItems }: Props<T>) {
  const { t } = useTranslation();
  const [filterMobileOpen, setFilterMobileOpen] = useState(false);

  const relevantFilters = filters.filter(({ isRelevant }) => isRelevant(filteredItems.byOtherFilters));

  const allSelectedOptions = filters.flatMap(({ selectedOptions }) => selectedOptions);

  const onTagUpdate = (option: FilterOption) => {
    const updatedFilter = filters.find(({ selectedOptions }) => selectedOptions.find(filterOptionsEqual(option)));
    if (!updatedFilter) {
      console.error("Could not remove filter option", option);
      return;
    }
    updatedFilter.onClickOption(option);
  };

  // Hide buttons if they have no possible effect
  if (relevantFilters.length < 1) return null;

  return (
    <>
      <DefaultContainer paddingInlineStart="0">
        <HideWithCSS below="sm">
          <Stack direction="row">
            {relevantFilters.map((filter: Filter<T>) => (
              <FilterMenu key={filter.key} filter={filter} filteredItems={filteredItems} />
            ))}
          </Stack>
        </HideWithCSS>
        <HideWithCSS above="sm">
          <Button
            variant="outline"
            leftIcon={filterMobileOpen ? <X /> : <Sliders style={{ transform: "rotate(90deg)" }} size="1em" />}
            marginBottom="0.5rem"
            // TODO: Should closing the filter on mobile also remove the filters?
            onClick={() => setFilterMobileOpen(!filterMobileOpen)}
          >
            {t("Filter")}
          </Button>
        </HideWithCSS>
      </DefaultContainer>
      {filterMobileOpen && relevantFilters.length > 1 && (
        <Accordion paddingTop="1rem" paddingBottom="1rem" allowMultiple allowToggle>
          {relevantFilters.map((filter: Filter<T>) => (
            <FilterMenuAccordion key={filter.key} filter={filter} filteredItems={filteredItems} />
          ))}
        </Accordion>
      )}
      {filterMobileOpen && relevantFilters.length === 1 && (
        <DefaultContainer paddingInlineStart="0rem" marginTop="0.5rem" marginBottom="1rem">
          <FilterOptions filter={relevantFilters[0]!} filteredItems={filteredItems} />
        </DefaultContainer>
      )}
      {!!allSelectedOptions.length && (
        <DefaultContainer marginTop={["0.5rem", "0"]}>
          <FilterTags items={allSelectedOptions} onTagUpdate={onTagUpdate} />
        </DefaultContainer>
      )}
    </>
  );
}

export default FilterButtons;
